import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../stores/authStore';
import { customFields as customFieldsApi, admin as adminApi } from '../../../services/api';
import { showSuccess, showError, showConfirm, showToast } from '../../../utils/alerts';
import { AdminPageHeader } from '../components/AdminPageHeader';
import { Pagination, usePagination } from '../../../components/ui/Pagination';

export interface UDFFieldItem {
  id: string;
  name: string;
  code: string;
  type: 'TEXT' | 'NUMBER' | 'SINGLE_SELECT' | 'MULTI_SELECT' | 'DATE' | 'BOOLEAN';
  category: 'SYSTEM' | 'ORG' | 'CUSTOM';
  description?: string;
  required: boolean;
  unique: boolean;
  inBulkUpload: boolean;
  inReports: boolean;
  maskInExports?: boolean;
  fillRate?: number;
  totalLearners?: number;
  filledCount?: number;
  isActive: boolean;
  isSystem: boolean;
  options: string[];
}

export const TenantUDFSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'PLATFORM_ADMIN' || user?.role === 'SUPER_ADMIN';

  const [tenantsList, setTenantsList] = useState<Array<{ id: string; name: string; slug: string }>>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>(user?.tenantId || '');

  const [activeTab, setActiveTab] = useState<'udf' | 'mapping' | 'csv' | 'privacy'>('udf');
  const [fields, setFields] = useState<UDFFieldItem[]>([]);
  const [quota, setQuota] = useState<{ activeCount: number; maxLimit: number }>({ activeCount: 0, maxLimit: 20 });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Selected field for right-side edit/create drawer
  const [selectedField, setSelectedField] = useState<UDFFieldItem | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(true);
  const [isCreatingNew, setIsCreatingNew] = useState<boolean>(false);
  const [newOptionInput, setNewOptionInput] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Tab 2: Mapping & Sync State
  const [selectedConnector, setSelectedConnector] = useState<'workday' | 'sap' | 'bamboohr' | 'azure_ad' | 'webhook'>('workday');
  const [syncSchedule, setSyncSchedule] = useState<'realtime' | 'hourly' | 'daily' | 'manual'>('daily');
  const [conflictResolution, setConflictResolution] = useState<'overwrite' | 'preserve' | 'append'>('overwrite');
  const [syncMappings, setSyncMappings] = useState<Array<{ source: string; target: string; isKey: boolean }>>([
    { source: 'Worker_ID', target: 'employeeId', isKey: true },
    { source: 'Legal_First_Name', target: 'firstName', isKey: false },
    { source: 'Legal_Last_Name', target: 'lastName', isKey: false },
    { source: 'Primary_Work_Email', target: 'email', isKey: false },
    { source: 'Job_Title_Descriptor', target: 'designation', isKey: false },
    { source: 'Supervisory_Org_Name', target: 'team', isKey: false },
    { source: 'Location_Region_Code', target: 'region', isKey: false },
    { source: 'Management_Level', target: 'grade', isKey: false },
  ]);

  // Tab 3: CSV Rules State
  const [unknownOptionStrategy, setUnknownOptionStrategy] = useState<'auto_create' | 'reject_row'>('auto_create');
  const [missingRequiredStrategy, setMissingRequiredStrategy] = useState<'skip_row' | 'abort_import'>('skip_row');
  const [autoTrimWhitespace, setAutoTrimWhitespace] = useState(true);
  const [autoLowercaseEmail, setAutoLowercaseEmail] = useState(true);
  const [dateFormat, setDateFormat] = useState<'YYYY-MM-DD' | 'DD/MM/YYYY' | 'MM/DD/YYYY'>('YYYY-MM-DD');
  const [headerAliases, setHeaderAliases] = useState<Array<{ header: string; mapsTo: string }>>([
    { header: 'Emp ID, Employee #, Staff ID, Badge', mapsTo: 'employeeId' },
    { header: 'Email, Work Email, E-mail', mapsTo: 'email' },
    { header: 'First Name, Given Name', mapsTo: 'firstName' },
    { header: 'Last Name, Surname, Family Name', mapsTo: 'lastName' },
    { header: 'Designation, Job Title, Role', mapsTo: 'designation' },
    { header: 'Team, Department, Division, Unit', mapsTo: 'team' },
    { header: 'Region, Territory, Branch, Zone', mapsTo: 'region' },
    { header: 'Grade, Level, Band', mapsTo: 'grade' },
  ]);

  // Tab 4: Governance & Masking State
  const [maskInExports, setMaskInExports] = useState<Record<string, boolean>>({
    employeeId: false,
    email: false,
    phone: true,
    dob: true,
    compensation_band: true,
  });
  const [maskInAiPrompts, setMaskInAiPrompts] = useState<Record<string, boolean>>({
    email: true,
    phone: true,
    employeeId: true,
    compensation_band: true,
  });
  const [autoAnonymizeOnDeactivation, setAutoAnonymizeOnDeactivation] = useState(true);

  // Load Tenants for Super Admin
  useEffect(() => {
    if (isSuperAdmin) {
      adminApi.tenants.list().then((res: any) => {
        if (res?.success && Array.isArray(res.data?.tenants)) {
          setTenantsList(res.data.tenants);
          if (!selectedTenantId && res.data.tenants.length > 0) {
            setSelectedTenantId(res.data.tenants[0].id);
          }
        }
      }).catch(err => console.warn('Could not load tenants list:', err));
    }
  }, [isSuperAdmin]);

  // Load fields
  useEffect(() => {
    fetchFields();
  }, [selectedTenantId]);

  const fetchFields = async () => {
    try {
      setIsLoading(true);
      const res = await customFieldsApi.list(selectedTenantId || undefined);
      if (res.success && res.data) {
        setFields(res.data.fields || []);
        setQuota(res.data.quota || { activeCount: (res.data.fields || []).filter((f: any) => f.isActive).length, maxLimit: 20 });
        if (!selectedField && (res.data.fields || []).length > 0) {
          setSelectedField(res.data.fields[1] || res.data.fields[0]);
        }
      }
    } catch (err: any) {
      showError('Failed to load custom fields: ' + (err.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleActive = async (field: UDFFieldItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (field.isSystem) return;
    try {
      const updated = { ...field, isActive: !field.isActive };
      setFields((prev) => prev.map((f) => (f.id === field.id ? updated : f)));
      if (selectedField?.id === field.id) {
        setSelectedField(updated);
      }
      await customFieldsApi.update(field.id, { isActive: updated.isActive }, selectedTenantId || undefined);
      setQuota((prev) => ({
        ...prev,
        activeCount: fields.filter((f) => (f.id === field.id ? updated.isActive : f.isActive)).length,
      }));
    } catch (err: any) {
      showError('Failed to toggle field status: ' + err.message);
      fetchFields();
    }
  };

  const handleSelectField = (field: UDFFieldItem) => {
    setSelectedField(JSON.parse(JSON.stringify(field)));
    setIsCreatingNew(false);
    setIsDrawerOpen(true);
  };

  const handleStartNewField = () => {
    const newField: UDFFieldItem = {
      id: '',
      name: '',
      code: '',
      type: 'SINGLE_SELECT',
      category: 'CUSTOM',
      description: '',
      required: false,
      unique: false,
      inBulkUpload: true,
      inReports: true,
      maskInExports: false,
      isActive: true,
      isSystem: false,
      options: ['Option 1', 'Option 2'],
    };
    setSelectedField(newField);
    setIsCreatingNew(true);
    setIsDrawerOpen(true);
    setActiveTab('udf');
  };

  const handleAddOption = () => {
    if (!newOptionInput.trim() || !selectedField) return;
    if (selectedField.options.includes(newOptionInput.trim())) {
      showError('Option already exists');
      return;
    }
    setSelectedField({
      ...selectedField,
      options: [...selectedField.options, newOptionInput.trim()],
    });
    setNewOptionInput('');
  };

  const handleRemoveOption = (index: number) => {
    if (!selectedField) return;
    const nextOpts = [...selectedField.options];
    nextOpts.splice(index, 1);
    setSelectedField({
      ...selectedField,
      options: nextOpts,
    });
  };

  const handleSaveField = async () => {
    if (!selectedField) return;
    if (!selectedField.name.trim()) {
      showError('Field name is required');
      return;
    }
    const autoCode = selectedField.code.trim() || selectedField.name.toLowerCase().replace(/[^a-z0-9]/g, '_');

    try {
      setIsSaving(true);
      if (isCreatingNew) {
        const res = await customFieldsApi.create(
          {
            ...selectedField,
            code: autoCode,
          },
          selectedTenantId || undefined
        );
        showSuccess('Custom field created successfully');
        await fetchFields();
        if (res.data) setSelectedField(res.data);
      } else {
        await customFieldsApi.update(
          selectedField.id,
          {
            ...selectedField,
            code: autoCode,
          },
          selectedTenantId || undefined
        );
        showSuccess('Custom field updated');
        setFields((prev) => prev.map((f) => (f.id === selectedField.id ? { ...selectedField, code: autoCode } : f)));
      }
      setIsCreatingNew(false);
    } catch (err: any) {
      showError('Failed to save custom field: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteField = async (field: UDFFieldItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (field.isSystem) return;

    const confirmed = await showConfirm({
      title: 'Delete Custom Field?',
      text: `Are you sure you want to delete "${field.name}"? Existing learner data for this attribute may be hidden from filters.`,
      confirmText: 'Delete Field',
      cancelText: 'Cancel',
      icon: 'warning',
    });
    if (!confirmed) return;

    try {
      await customFieldsApi.delete(field.id, selectedTenantId || undefined);
      showSuccess(`Field "${field.name}" deleted.`);
      setFields((prev) => prev.filter((f) => f.id !== field.id));
      if (selectedField?.id === field.id) {
        setSelectedField(null);
        setIsDrawerOpen(false);
      }
    } catch (err: any) {
      showError('Failed to delete field: ' + err.message);
    }
  };

  const handleExportJson = () => {
    const payload = {
      tenantId: 'current_tenant',
      exportedAt: new Date().toISOString(),
      udfFields: fields,
      mappingRules: syncMappings,
      csvRules: { unknownOptionStrategy, missingRequiredStrategy, dateFormat, headerAliases },
      governance: { maskInExports, maskInAiPrompts, autoAnonymizeOnDeactivation },
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `trainova_udf_and_governance_schema.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showSuccess('Exported UDF & Governance schema JSON');
  };

  // Grouped Fields Filtering
  const filteredFields = useMemo(() => {
    if (!searchQuery.trim()) return fields;
    const q = searchQuery.toLowerCase();
    return fields.filter((f) => f.name.toLowerCase().includes(q) || f.code.toLowerCase().includes(q) || (f.description && f.description.toLowerCase().includes(q)));
  }, [fields, searchQuery]);

  const {
    currentPage: udfPage,
    pageSize: udfPageSize,
    totalItems: udfTotalItems,
    paginatedItems: paginatedFields,
    goToPage: setUdfPage,
    setPageSize: setUdfPageSize,
  } = usePagination(filteredFields, 1, 20);

  const quotaPercent = Math.min(100, Math.round((quota.activeCount / quota.maxLimit) * 100));

  return (
    <div className="admin-page">
      {/* 1. Standardized Admin Header & Banner */}
      <AdminPageHeader
        breadcrumbs={[
          { label: 'Admin', to: '/admin' },
          { label: 'Settings', to: '/admin/settings' },
          { label: 'Custom Fields & UDF Mapping' },
        ]}
        icon="🏷️"
        title="Learner Custom Fields (UDF) & Taxonomy"
        badge="Schema Taxonomy"
        description="Configure custom user attributes, dropdown taxonomy, CSV bulk import mappings, API sync rules, and enterprise data masking."
        actions={
          <div className="udf-header-actions">
            <button
              type="button"
              onClick={handleExportJson}
              className="udf-header-btn udf-header-btn--export"
            >
              📥 Export Schema
            </button>
            <button
              type="button"
              onClick={handleStartNewField}
              className="udf-header-btn udf-header-btn--primary"
            >
              ➕ Add Custom Field
            </button>
          </div>
        }
      />

      {/* Super Admin Tenant Switcher */}
      {isSuperAdmin && (
        <div
          className="udf-scope-banner"
          style={{
            background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.05) 0%, rgba(30, 58, 138, 0.08) 100%)',
            border: '1px solid rgba(37, 99, 235, 0.2)',
            borderRadius: '12px',
            padding: '14px 20px',
            marginBottom: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '20px' }}>🏢</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: '13.5px', color: '#1e3a8a' }}>
                Super Admin Scope: Active Organization Taxonomy
              </div>
              <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                Viewing and editing Custom Fields (UDFs) specific to the selected enterprise tenant.
              </div>
            </div>
          </div>
          <div className="udf-scope-controls" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ fontSize: '13px', fontWeight: 600, color: '#1e3a8a' }}>Organization:</label>
            <select
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              className="admin-form__select udf-scope-select"
              style={{ minWidth: '220px', maxWidth: '100%', padding: '8px 12px', fontSize: '13px', borderColor: '#93c5fd', background: '#fff' }}
            >
              {tenantsList.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.slug || t.id})
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* 2. Top Navigation Tabs */}
      <div className="udf-tabs-container">
        {[
          { id: 'udf', label: 'User Attributes (UDFs)' },
          { id: 'mapping', label: 'Field Mapping & API Sync' },
          { id: 'csv', label: 'CSV Bulk Upload Rules' },
          { id: 'privacy', label: 'Data Governance & Masking' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setActiveTab(t.id as any)}
            className={`udf-tab-btn ${activeTab === t.id ? 'active' : ''}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* 3. TAB 1: User Attributes (UDFs) */}
      {activeTab === 'udf' && (
        <div className="udf-grid-layout udf-grid-layout--udf">
          {/* LEFT COLUMN: Field Quota & Table */}
          <div className="udf-card" style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)', overflow: 'hidden' }}>
            {/* Quota & Search Bar */}
            <div className="udf-quota-search-bar" style={{ padding: '14px 20px', borderBottom: '1px solid #F1F5F9', background: '#FAFBFE', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
              <div className="udf-quota-info" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '13px', color: '#475569' }}>
                  Field Quota: <b style={{ color: '#0F172A' }}>{quota.activeCount} of {quota.maxLimit}</b> custom fields active
                </span>
                <div style={{ width: '120px', height: '6px', borderRadius: '999px', background: '#E2E8F0', overflow: 'hidden' }}>
                  <div style={{ width: `${quotaPercent}%`, height: '100%', background: quotaPercent > 80 ? '#F59E0B' : '#10B981', borderRadius: '999px' }} />
                </div>
              </div>

              <div className="udf-search-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="text"
                  placeholder="Search fields by name or key..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="udf-search-input"
                  style={{
                    height: '32px',
                    padding: '0 10px',
                    borderRadius: '7px',
                    border: '1px solid #CBD5E1',
                    fontSize: '12.5px',
                    outline: 'none',
                    width: '220px',
                  }}
                />
              </div>
            </div>

            {/* Table */}
            <div className="udf-table-container">
              <table className="udf-table udf-table--main" style={{ width: '100%', minWidth: '560px', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#F8FAFC', borderBottom: '1px solid #E2E8F0', color: '#64748B', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    <th style={{ padding: '10px 14px', textAlign: 'left', width: '30px' }}></th>
                    <th style={{ padding: '10px 12px', textAlign: 'left' }}>Field Name & Key</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left' }}>Data Type</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', width: '70px' }}>Required</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', width: '130px' }}>Fill Rate</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', width: '70px' }}>Status</th>
                    <th style={{ padding: '10px 14px', textAlign: 'right', width: '60px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && (
                    <tr>
                      <td colSpan={7} style={{ padding: '36px', textAlign: 'center', color: '#64748B' }}>
                        Loading taxonomy schema...
                      </td>
                    </tr>
                  )}

                  {!isLoading && filteredFields.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ padding: '36px', textAlign: 'center', color: '#64748B' }}>
                        No fields match your search filter.
                      </td>
                    </tr>
                  )}

                  {!isLoading &&
                    paginatedFields.map((f) => {
                      const isSelected = selectedField?.id === f.id && isDrawerOpen;
                      const fillPct = f.fillRate ?? 0;
                      return (
                        <tr
                          key={f.id || f.code}
                          onClick={() => handleSelectField(f)}
                          style={{
                            borderBottom: '1px solid #F1F5F9',
                            background: isSelected ? '#EFF6FF' : '#FFFFFF',
                            cursor: 'pointer',
                            transition: 'background 0.12s ease',
                          }}
                        >
                          <td style={{ padding: '10px 14px', color: '#94A3B8' }}>
                            {f.isSystem ? '🔒' : '⋮⋮'}
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ fontWeight: 600, color: '#0F172A' }}>{f.name}</span>
                              {f.isSystem && (
                                <span style={{ fontSize: '10px', background: '#F1F5F9', color: '#475569', padding: '1px 6px', borderRadius: '4px', fontWeight: 600 }}>SYSTEM</span>
                              )}
                            </div>
                            <code style={{ fontSize: '11px', color: '#64748B' }}>{f.code}</code>
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            <span style={{ fontSize: '12px', color: '#334155', background: '#F8FAFC', border: '1px solid #E2E8F0', padding: '2px 8px', borderRadius: '6px' }}>
                              {f.type}
                            </span>
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                            {f.required ? (
                              <span style={{ color: '#DC2626', fontWeight: 700 }}>Yes</span>
                            ) : (
                              <span style={{ color: '#94A3B8' }}>No</span>
                            )}
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ flex: 1, height: '5px', background: '#E2E8F0', borderRadius: '999px', overflow: 'hidden' }}>
                                <div style={{ width: `${fillPct}%`, height: '100%', background: '#0D4FBD', borderRadius: '999px' }} />
                              </div>
                              <span style={{ fontSize: '11.5px', color: '#64748B', width: '32px' }}>{fillPct}%</span>
                            </div>
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                            <button
                              type="button"
                              onClick={(e) => handleToggleActive(f, e)}
                              disabled={f.isSystem}
                              style={{
                                padding: '3px 8px',
                                borderRadius: '6px',
                                fontSize: '11px',
                                fontWeight: 700,
                                border: 'none',
                                cursor: f.isSystem ? 'default' : 'pointer',
                                background: f.isActive ? '#DCFCE7' : '#F1F5F9',
                                color: f.isActive ? '#15803D' : '#64748B',
                              }}
                            >
                              {f.isActive ? 'Active' : 'Inactive'}
                            </button>
                          </td>
                          <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                            {!f.isSystem && (
                              <button
                                type="button"
                                onClick={(e) => handleDeleteField(f, e)}
                                title="Delete Field"
                                style={{ background: 'transparent', border: 'none', color: '#94A3B8', cursor: 'pointer', fontSize: '14px', padding: '2px 6px' }}
                              >
                                🗑️
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
              {udfTotalItems > 0 && (
                <Pagination
                  currentPage={udfPage}
                  totalItems={udfTotalItems}
                  pageSize={udfPageSize}
                  onPageChange={setUdfPage}
                  onPageSizeChange={setUdfPageSize}
                  showPageSizeSelector
                  pageSizeOptions={[10, 20, 50, 100]}
                  itemLabel="fields"
                />
              )}
            </div>
          </div>

          {/* RIGHT COLUMN: Live Editor Drawer */}
          {selectedField ? (
            <div className="udf-card udf-editor-card" style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '14px', boxShadow: '0 4px 14px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #F1F5F9', background: '#FAFBFE', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#0F172A' }}>
                    {isCreatingNew ? 'Create New Custom Field' : selectedField.name}
                  </h3>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748B' }}>
                    {isCreatingNew ? 'Define attribute data type and options' : `Field key: ${selectedField.code}`}
                  </p>
                </div>
                {selectedField.isSystem && (
                  <span style={{ fontSize: '11px', background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', padding: '2px 8px', borderRadius: '6px', fontWeight: 600 }}>
                    Protected System Field
                  </span>
                )}
              </div>

              <div className="udf-editor-body" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px', maxHeight: '720px', overflowY: 'auto' }}>
                {/* Field Name */}
                <div className="admin-form__group" style={{ margin: 0 }}>
                  <label className="admin-form__label" style={{ fontSize: '12px', fontWeight: 600 }}>Field Display Name *</label>
                  <input
                    type="text"
                    disabled={selectedField.isSystem}
                    value={selectedField.name}
                    onChange={(e) => setSelectedField({ ...selectedField, name: e.target.value })}
                    placeholder="e.g. Branch Location, Certification Level"
                    className="admin-form__input udf-form-input"
                    style={{ width: '100%', height: '38px', borderRadius: '8px', border: '1px solid #CBD5E1', padding: '0 12px' }}
                  />
                </div>

                {/* Field Code / Key */}
                <div className="admin-form__group" style={{ margin: 0 }}>
                  <label className="admin-form__label" style={{ fontSize: '12px', fontWeight: 600 }}>API / CSV Code Key</label>
                  <input
                    type="text"
                    disabled={selectedField.isSystem}
                    value={selectedField.code}
                    onChange={(e) => setSelectedField({ ...selectedField, code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                    placeholder="e.g. branch_location"
                    className="admin-form__input udf-form-input"
                    style={{ width: '100%', height: '38px', borderRadius: '8px', border: '1px solid #CBD5E1', padding: '0 12px', fontFamily: 'monospace' }}
                  />
                </div>

                {/* Data Type */}
                <div className="admin-form__group" style={{ margin: 0 }}>
                  <label className="admin-form__label" style={{ fontSize: '12px', fontWeight: 600 }}>Data Type</label>
                  <select
                    disabled={selectedField.isSystem}
                    value={selectedField.type}
                    onChange={(e) => setSelectedField({ ...selectedField, type: e.target.value as any })}
                    className="admin-form__select udf-form-select"
                    style={{ width: '100%', height: '38px', borderRadius: '8px', border: '1px solid #CBD5E1', padding: '0 10px', background: '#FFFFFF' }}
                  >
                    <option value="TEXT">Short Text (string)</option>
                    <option value="NUMBER">Number (integer / decimal)</option>
                    <option value="SINGLE_SELECT">Single Select (Dropdown list)</option>
                    <option value="MULTI_SELECT">Multi Select (Multiple tags)</option>
                    <option value="DATE">Date (ISO 8601)</option>
                    <option value="BOOLEAN">Boolean (Yes / No toggle)</option>
                  </select>
                </div>

                {/* Dropdown Options (for Select types) */}
                {(selectedField.type === 'SINGLE_SELECT' || selectedField.type === 'MULTI_SELECT') && (
                  <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '14px' }}>
                    <label style={{ fontSize: '12px', fontWeight: 700, color: '#0F172A', display: 'block', marginBottom: '8px' }}>
                      Allowed Dropdown Options ({selectedField.options.length})
                    </label>

                    <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                      <input
                        type="text"
                        disabled={selectedField.isSystem}
                        placeholder="Add new option value..."
                        value={newOptionInput}
                        onChange={(e) => setNewOptionInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddOption();
                          }
                        }}
                        className="admin-form__input udf-form-input"
                        style={{ flex: 1, minWidth: 0, height: '38px', borderRadius: '6px', border: '1px solid #CBD5E1', padding: '0 10px' }}
                      />
                      <button
                        type="button"
                        disabled={selectedField.isSystem}
                        onClick={handleAddOption}
                        className="udf-opt-add-btn"
                        style={{ flexShrink: 0, minHeight: '38px', padding: '0 16px', borderRadius: '6px', background: '#2563EB', color: '#FFFFFF', border: 'none', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
                      >
                        Add
                      </button>
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '140px', overflowY: 'auto' }}>
                      {selectedField.options.map((opt, idx) => (
                        <span
                          key={idx}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: '#FFFFFF',
                            border: '1px solid #CBD5E1',
                            borderRadius: '6px',
                            padding: '3px 8px',
                            fontSize: '12px',
                            color: '#334155',
                            maxWidth: '100%',
                            wordBreak: 'break-word',
                          }}
                        >
                          <span style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {opt}
                          </span>
                          {!selectedField.isSystem && (
                            <button
                              type="button"
                              onClick={() => handleRemoveOption(idx)}
                              style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#94A3B8', fontSize: '12px', padding: 0, flexShrink: 0 }}
                            >
                              ✕
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Field Toggles & Validation */}
                <div className="udf-toggles-grid">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: '#334155', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      disabled={selectedField.isSystem}
                      checked={selectedField.required}
                      onChange={(e) => setSelectedField({ ...selectedField, required: e.target.checked })}
                    />
                    <b>Mandatory Field</b>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: '#334155', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      disabled={selectedField.isSystem}
                      checked={selectedField.unique}
                      onChange={(e) => setSelectedField({ ...selectedField, unique: e.target.checked })}
                    />
                    <b>Unique Value</b>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: '#334155', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedField.inBulkUpload}
                      onChange={(e) => setSelectedField({ ...selectedField, inBulkUpload: e.target.checked })}
                    />
                    Allow in CSV Import
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: '#334155', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedField.inReports}
                      onChange={(e) => setSelectedField({ ...selectedField, inReports: e.target.checked })}
                    />
                    Include in Reports
                  </label>
                </div>
              </div>

              {/* Drawer Footer Actions */}
              {!selectedField.isSystem && (
                <div className="udf-drawer-actions" style={{ padding: '14px 20px', borderTop: '1px solid #F1F5F9', background: '#FAFBFE', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={() => setIsDrawerOpen(false)}
                    style={{ padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, background: '#FFFFFF', border: '1px solid #CBD5E1', color: '#475569', cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={isSaving}
                    onClick={handleSaveField}
                    style={{
                      padding: '8px 18px',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: 700,
                      background: '#0D4FBD',
                      color: '#FFFFFF',
                      border: 'none',
                      cursor: isSaving ? 'not-allowed' : 'pointer',
                      boxShadow: '0 2px 6px rgba(13, 79, 189, 0.25)',
                    }}
                  >
                    {isSaving ? 'Saving...' : isCreatingNew ? 'Create Custom Field' : 'Save Changes'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div style={{ background: '#FAFBFE', border: '1px dashed #CBD5E1', borderRadius: '14px', padding: '40px 20px', textAlign: 'center', color: '#64748B' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>📋</div>
              <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#0F172A', margin: '0 0 4px' }}>No Field Selected</h3>
              <p style={{ fontSize: '12.5px', margin: '0 0 14px' }}>Click any field on the left table to edit its taxonomy and rules, or create a new custom field.</p>
              <button
                type="button"
                onClick={handleStartNewField}
                style={{ padding: '8px 16px', borderRadius: '8px', background: '#0D4FBD', color: '#FFFFFF', border: 'none', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer' }}
              >
                + Add Custom Field
              </button>
            </div>
          )}
        </div>
      )}

      {/* 4. TAB 2: Field Mapping & API Sync */}
      {activeTab === 'mapping' && (
        <div className="udf-grid-layout udf-grid-layout--side380">
          <div className="udf-card-padded" style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '14px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A', margin: '0 0 4px' }}>
                HRIS / SCIM 2.0 Inbound Attribute Mapping
              </h3>
              <p style={{ fontSize: '13px', color: '#64748B', margin: 0 }}>
                Map external system attributes from your corporate identity provider or HR system directly to Trainova UDFs.
              </p>
            </div>

            {/* Provider Selector */}
            <div className="udf-provider-selector" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {[
                { id: 'workday', label: 'Workday Human Capital', icon: '🏢' },
                { id: 'sap', label: 'SAP SuccessFactors', icon: '🌐' },
                { id: 'bamboohr', label: 'BambooHR', icon: '🎋' },
                { id: 'azure_ad', label: 'Microsoft Entra ID', icon: '🔐' },
                { id: 'webhook', label: 'Custom SCIM / Webhook', icon: '⚡' },
              ].map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    setSelectedConnector(c.id as any);
                    showToast(`Switched schema template to ${c.label}`, 'info');
                  }}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: selectedConnector === c.id ? 700 : 500,
                    border: selectedConnector === c.id ? '1.5px solid #2563EB' : '1px solid #CBD5E1',
                    background: selectedConnector === c.id ? '#EFF6FF' : '#FFFFFF',
                    color: selectedConnector === c.id ? '#1D4ED8' : '#334155',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <span>{c.icon}</span>
                  <span>{c.label}</span>
                </button>
              ))}
            </div>

            {/* Mapping Table */}
            <div className="udf-table-container udf-table-container--bordered">
              <table className="udf-table udf-table--mapping" style={{ width: '100%', minWidth: '520px', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#F8FAFC', borderBottom: '1px solid #E2E8F0', color: '#64748B', fontSize: '11.5px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    <th style={{ padding: '10px 14px', textAlign: 'left' }}>Source Field ({selectedConnector.toUpperCase()})</th>
                    <th style={{ padding: '10px 14px', textAlign: 'center', width: '40px' }}>➜</th>
                    <th style={{ padding: '10px 14px', textAlign: 'left' }}>Trainova Target Attribute</th>
                    <th style={{ padding: '10px 14px', textAlign: 'center', width: '100px' }}>Primary Key</th>
                  </tr>
                </thead>
                <tbody>
                  {syncMappings.map((m, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #F1F5F9' }}>
                      <td style={{ padding: '10px 14px' }}>
                        <code style={{ background: '#F1F5F9', padding: '2px 6px', borderRadius: '4px', color: '#0F172A', fontWeight: 600 }}>{m.source}</code>
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'center', color: '#94A3B8' }}>➜</td>
                      <td style={{ padding: '10px 14px' }}>
                        <select
                          value={m.target}
                          onChange={(e) => {
                            const next = [...syncMappings];
                            next[idx].target = e.target.value;
                            setSyncMappings(next);
                          }}
                          style={{ height: '32px', padding: '0 8px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '12.5px', width: '100%', background: '#FFFFFF' }}
                        >
                          <option value="employeeId">Employee ID (employeeId)</option>
                          <option value="email">Email Address (email)</option>
                          <option value="firstName">First Name (firstName)</option>
                          <option value="lastName">Last Name (lastName)</option>
                          <option value="designation">Designation (designation)</option>
                          <option value="team">Team / Department (team)</option>
                          <option value="region">Region / Hub (region)</option>
                          <option value="grade">Grade / Band (grade)</option>
                          {fields.filter(f => !f.isSystem).map(f => (
                            <option key={f.code} value={f.code}>{f.name} ({f.code})</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                        <input
                          type="radio"
                          name="primaryKey"
                          checked={m.isKey}
                          onChange={() => {
                            setSyncMappings(syncMappings.map((item, i) => ({ ...item, isKey: i === idx })));
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="udf-tab-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                onClick={() => showSuccess('API and HRIS mapping rules saved successfully!')}
                className="udf-submit-btn"
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 700,
                  background: '#2563EB',
                  color: '#FFFFFF',
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(37, 99, 235, 0.3)',
                }}
              >
                Save Sync Mapping
              </button>
            </div>
          </div>

          {/* Sync Engine Settings Drawer */}
          <div className="udf-card-side" style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#0F172A' }}>
              ⚙️ Synchronization Policy
            </h4>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                Sync Frequency
              </label>
              <select
                value={syncSchedule}
                onChange={(e) => setSyncSchedule(e.target.value as any)}
                style={{ width: '100%', height: '34px', borderRadius: '6px', border: '1px solid #CBD5E1', padding: '0 8px', fontSize: '12.5px' }}
              >
                <option value="realtime">Real-time Webhook Push</option>
                <option value="hourly">Hourly Differential Sync</option>
                <option value="daily">Daily Midnight Batch (02:00 UTC)</option>
                <option value="manual">Manual Execution Only</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                Conflict Strategy
              </label>
              <select
                value={conflictResolution}
                onChange={(e) => setConflictResolution(e.target.value as any)}
                style={{ width: '100%', height: '34px', borderRadius: '6px', border: '1px solid #CBD5E1', padding: '0 8px', fontSize: '12.5px' }}
              >
                <option value="overwrite">Source of Truth: Always Overwrite Portal</option>
                <option value="preserve">Preserve Portal Edits if newer</option>
                <option value="append">Append / Union Only</option>
              </select>
            </div>

            <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '12px' }}>
              <span style={{ fontSize: '11.5px', fontWeight: 700, color: '#334155', textTransform: 'uppercase' }}>Inbound Webhook URL</span>
              <code style={{ display: 'block', fontSize: '11px', color: '#0D4FBD', wordBreak: 'break-all', marginTop: '4px', padding: '6px', background: '#FFFFFF', border: '1px solid #CBD5E1', borderRadius: '4px' }}>
                https://api.trainova.ai/v1/scim/v2/tenants/current/Users
              </code>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText('https://api.trainova.ai/v1/scim/v2/tenants/current/Users');
                  showToast('Webhook endpoint URL copied to clipboard', 'info');
                }}
                style={{ marginTop: '8px', padding: '4px 10px', fontSize: '11.5px', fontWeight: 600, borderRadius: '6px', border: '1px solid #CBD5E1', background: '#FFFFFF', cursor: 'pointer' }}
              >
                📋 Copy Endpoint URL
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 5. TAB 3: CSV Bulk Upload Rules */}
      {activeTab === 'csv' && (
        <div className="udf-grid-layout udf-grid-layout--side380">
          <div className="udf-card-padded" style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '14px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A', margin: '0 0 4px' }}>
                CSV Header Aliases & Auto-Detection Dictionary
              </h3>
              <p style={{ fontSize: '13px', color: '#64748B', margin: 0 }}>
                Trainova will automatically map CSV column headers matching any of these aliases during bulk member upload.
              </p>
            </div>

            <div className="udf-table-container udf-table-container--bordered">
              <table className="udf-table udf-table--csv" style={{ width: '100%', minWidth: '480px', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#F8FAFC', borderBottom: '1px solid #E2E8F0', color: '#64748B', fontSize: '11.5px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    <th style={{ padding: '10px 14px', textAlign: 'left' }}>Recognized Header Variations (Comma Separated)</th>
                    <th style={{ padding: '10px 14px', textAlign: 'center', width: '40px' }}>➜</th>
                    <th style={{ padding: '10px 14px', textAlign: 'left', width: '220px' }}>Mapped Attribute</th>
                  </tr>
                </thead>
                <tbody>
                  {headerAliases.map((a, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #F1F5F9' }}>
                      <td style={{ padding: '10px 14px' }}>
                        <input
                          type="text"
                          value={a.header}
                          onChange={(e) => {
                            const next = [...headerAliases];
                            next[idx].header = e.target.value;
                            setHeaderAliases(next);
                          }}
                          className="admin-form__input udf-csv-alias-input"
                          style={{ width: '100%', height: '36px', padding: '0 10px', borderRadius: '6px', border: '1px solid #CBD5E1' }}
                        />
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'center', color: '#94A3B8' }}>➜</td>
                      <td style={{ padding: '10px 14px' }}>
                        <code style={{ fontWeight: 700, color: '#0F172A' }}>{a.mapsTo}</code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="udf-tab-actions" style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => showSuccess('CSV header detection dictionary updated!')}
                className="udf-submit-btn"
                style={{ padding: '10px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: 700, background: '#2563EB', color: '#FFFFFF', border: 'none', cursor: 'pointer' }}
              >
                Save CSV Alias Rules
              </button>
            </div>
          </div>

          {/* CSV Parser Strictness Drawer */}
          <div className="udf-card-side" style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#0F172A' }}>
              🛡️ Bulk Import Validation Engine
            </h4>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                Unknown Dropdown Option Strategy
              </label>
              <select
                value={unknownOptionStrategy}
                onChange={(e) => setUnknownOptionStrategy(e.target.value as any)}
                style={{ width: '100%', height: '34px', borderRadius: '6px', border: '1px solid #CBD5E1', padding: '0 8px', fontSize: '12.5px' }}
              >
                <option value="auto_create">Auto-append new option to field schema</option>
                <option value="reject_row">Reject row with validation error</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                Missing Required Fields Strategy
              </label>
              <select
                value={missingRequiredStrategy}
                onChange={(e) => setMissingRequiredStrategy(e.target.value as any)}
                style={{ width: '100%', height: '34px', borderRadius: '6px', border: '1px solid #CBD5E1', padding: '0 8px', fontSize: '12.5px' }}
              >
                <option value="skip_row">Skip invalid rows & import valid learners</option>
                <option value="abort_import">Atomic Rollback: Abort entire import</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                Date Format Parser
              </label>
              <select
                value={dateFormat}
                onChange={(e) => setDateFormat(e.target.value as any)}
                style={{ width: '100%', height: '34px', borderRadius: '6px', border: '1px solid #CBD5E1', padding: '0 8px', fontSize: '12.5px' }}
              >
                <option value="YYYY-MM-DD">YYYY-MM-DD (Standard ISO)</option>
                <option value="DD/MM/YYYY">DD/MM/YYYY (UK / APAC / India)</option>
                <option value="MM/DD/YYYY">MM/DD/YYYY (US Standard)</option>
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingTop: '8px', borderTop: '1px solid #F1F5F9' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: '#334155', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={autoTrimWhitespace}
                  onChange={(e) => setAutoTrimWhitespace(e.target.checked)}
                />
                Auto-trim leading/trailing whitespace
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: '#334155', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={autoLowercaseEmail}
                  onChange={(e) => setAutoLowercaseEmail(e.target.checked)}
                />
                Normalize emails to lowercase
              </label>
            </div>
          </div>
        </div>
      )}

      {/* 6. TAB 4: Data Governance & Masking */}
      {activeTab === 'privacy' && (
        <div className="udf-grid-layout udf-grid-layout--side380">
          <div className="udf-card-padded" style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '14px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A', margin: '0 0 4px' }}>
                PII Privacy & Attribute Masking Policies
              </h3>
              <p style={{ fontSize: '13px', color: '#64748B', margin: 0 }}>
                Control which learner attributes are sanitized or masked in CSV/PDF analytical exports and LLM prompt tokens.
              </p>
            </div>

            <div className="udf-table-container udf-table-container--bordered">
              <table className="udf-table udf-table--privacy" style={{ width: '100%', minWidth: '560px', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#F8FAFC', borderBottom: '1px solid #E2E8F0', color: '#64748B', fontSize: '11.5px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    <th style={{ padding: '10px 14px', textAlign: 'left' }}>Attribute Name</th>
                    <th style={{ padding: '10px 14px', textAlign: 'left' }}>Data Classification</th>
                    <th style={{ padding: '10px 14px', textAlign: 'center', width: '130px' }}>Mask in Exports</th>
                    <th style={{ padding: '10px 14px', textAlign: 'center', width: '130px' }}>Mask in LLM Prompts</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { code: 'email', name: 'Email Address', type: 'PII (Contact)' },
                    { code: 'employeeId', name: 'Employee ID', type: 'Identifier' },
                    { code: 'phone', name: 'Phone Number', type: 'Sensitive PII' },
                    { code: 'dob', name: 'Date of Birth', type: 'Sensitive PII' },
                    { code: 'designation', name: 'Designation / Title', type: 'General' },
                    { code: 'team', name: 'Department / Unit', type: 'General' },
                    { code: 'region', name: 'Region Hub', type: 'General' },
                  ].map((item) => (
                    <tr key={item.code} style={{ borderBottom: '1px solid #F1F5F9' }}>
                      <td style={{ padding: '10px 14px' }}>
                        <strong style={{ color: '#0F172A' }}>{item.name}</strong>
                        <code style={{ display: 'block', fontSize: '11px', color: '#64748B' }}>{item.code}</code>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ fontSize: '11.5px', background: item.type.includes('PII') ? '#FEF2F2' : '#F1F5F9', color: item.type.includes('PII') ? '#B91C1C' : '#475569', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>
                          {item.type}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={!!maskInExports[item.code]}
                          onChange={(e) => setMaskInExports({ ...maskInExports, [item.code]: e.target.checked })}
                        />
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={!!maskInAiPrompts[item.code]}
                          onChange={(e) => setMaskInAiPrompts({ ...maskInAiPrompts, [item.code]: e.target.checked })}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="udf-tab-actions" style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => showSuccess('Data privacy and masking rules saved successfully!')}
                className="udf-submit-btn"
                style={{ padding: '10px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: 700, background: '#2563EB', color: '#FFFFFF', border: 'none', cursor: 'pointer' }}
              >
                Save Governance Policies
              </button>
            </div>
          </div>

          {/* Compliance Card */}
          <div className="udf-card-side" style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#0F172A' }}>
              🔒 Regulatory Compliance Matrix
            </h4>

            <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: '8px', padding: '12px' }}>
              <div style={{ fontWeight: 700, color: '#065F46', fontSize: '12.5px' }}>✓ GDPR Article 17 Compliant</div>
              <p style={{ margin: '4px 0 0', fontSize: '11.5px', color: '#047857' }}>
                Automated Right to Erasure: Soft-delete triggers cryptographic erasure of learner PII after 90 days.
              </p>
            </div>

            <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '8px', padding: '12px' }}>
              <div style={{ fontWeight: 700, color: '#1E40AF', fontSize: '12.5px' }}>✓ SOC 2 Type II Certified</div>
              <p style={{ margin: '4px 0 0', fontSize: '11.5px', color: '#1D4ED8' }}>
                All schema alterations, UDF additions, and sync operations are recorded in the immutable audit log.
              </p>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: '#334155', cursor: 'pointer', paddingTop: '6px' }}>
              <input
                type="checkbox"
                checked={autoAnonymizeOnDeactivation}
                onChange={(e) => setAutoAnonymizeOnDeactivation(e.target.checked)}
              />
              Auto-anonymize on member offboarding
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantUDFSettingsPage;
